import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StopWithDetails } from '../../../../../types';
import { StopPlaceSignType } from '../../../../../types/stop-registry';
import { showSuccessToast, submitFormByRef } from '../../../../../utils';
import {
  InfoContainer,
  useInfoContainerControls,
} from '../../../../common/InfoContainer';
import { stopInfoContainerColors } from '../Common/stopInfoContainerColors';
import { SignageDetailsFormState } from './schema';
import { SignageDetailsForm } from './SignageDetailsForm';
import { SignageDetailsViewCard } from './SignageDetailsViewCard';
import { useEditStopSignageDetails } from './useEditStopSignageDetails';

type SignageDetailsSectionProps = {
  readonly stop: StopWithDetails;
};

function mapSignageDetailsToFormState(
  stop: StopWithDetails,
): Partial<SignageDetailsFormState> {
  const generalSign = stop.quay?.placeEquipments?.generalSign?.[0];
  const signType = generalSign?.privateCode?.value;

  return {
    signType: signType ? (signType as StopPlaceSignType) : undefined,
    numberOfFrames: generalSign?.numberOfFrames,
    replacesRailSign: generalSign?.replacesRailSign,
    signageInstructionExceptions: generalSign?.note?.value,
  };
}

export const SignageDetailsSection: FC<SignageDetailsSectionProps> = ({
  stop,
}) => {
  const { t } = useTranslation();

  const { saveStopPlaceSignageDetails, defaultErrorHandler } =
    useEditStopSignageDetails();

  const formRef = useRef<ExplicitAny>(null);
  const infoContainerControls = useInfoContainerControls({
    isExpandable: true,
    isEditable: true,
    onSave: () => submitFormByRef(formRef),
  });

  const onSubmit = async (state: SignageDetailsFormState) => {
    try {
      await saveStopPlaceSignageDetails({ state, stop });

      showSuccessToast(t(($) => $.stops.editSuccess));
      infoContainerControls.setIsInEditMode(false);
    } catch (err) {
      defaultErrorHandler(err as Error);
    }
  };

  const defaultValues = mapSignageDetailsToFormState(stop);

  return (
    <InfoContainer
      colors={stopInfoContainerColors}
      controls={infoContainerControls}
      title={t(($) => $.stopDetails.signs.title)}
      ariaLabel={t(($) => $.stopDetails.signs.title)}
      testIdPrefix="SignageDetailsSection"
    >
      {infoContainerControls.isInEditMode && !!defaultValues ? (
        <SignageDetailsForm
          defaultValues={defaultValues}
          ref={formRef}
          onSubmit={onSubmit}
          onCancel={() => infoContainerControls.setIsInEditMode(false)}
          testIdPrefix="SignageDetailsSection"
        />
      ) : (
        <SignageDetailsViewCard stop={stop} />
      )}
    </InfoContainer>
  );
};
